/**
 * browserLogger.js — Sentry-like structured logger for the React frontend.
 *
 * Auto-enriches every log with:
 *  - Browser context (name, version, OS, user agent, screen size)
 *  - Page context (URL, referrer, route)
 *  - User context (email from Redux store, session ID)
 *  - Trace context (trace_id, span_id from active OTEL span)
 *  - Stack traces (auto-captured for error/warn level)
 *  - Breadcrumbs (navigation, fetch, clicks — ring buffer, max 50)
 */

import { logger } from './otelHelper';
import { SeverityNumber } from '@opentelemetry/api-logs';
import { trace, context } from '@opentelemetry/api';
import store from '../redux/store';

// ─── Browser Context ─────────────────────────────────────────────
function parseBrowser(ua) {
    // Lightweight UA parser — covers Chrome, Firefox, Safari, Edge
    const browsers = [
        { name: 'Edge', regex: /Edg\/(\d+[\d.]*)/ },
        { name: 'Chrome', regex: /Chrome\/(\d+[\d.]*)/ },
        { name: 'Firefox', regex: /Firefox\/(\d+[\d.]*)/ },
        { name: 'Safari', regex: /Version\/(\d+[\d.]*).*Safari/ },
    ];
    for (const b of browsers) {
        const match = ua.match(b.regex);
        if (match) return { name: b.name, version: match[1] };
    }
    return { name: 'Unknown', version: 'Unknown' };
}

function parseOS(ua) {
    if (/Windows NT 10/.test(ua)) return 'Windows 10';
    if (/Windows NT/.test(ua)) return 'Windows';
    if (/Mac OS X ([\d_]+)/.test(ua)) return `macOS ${ua.match(/Mac OS X ([\d_]+)/)[1].replace(/_/g, '.')}`;
    if (/Linux/.test(ua)) return 'Linux';
    if (/Android ([\d.]+)/.test(ua)) return `Android ${ua.match(/Android ([\d.]+)/)[1]}`;
    if (/iPhone OS ([\d_]+)/.test(ua)) return `iOS ${ua.match(/iPhone OS ([\d_]+)/)[1].replace(/_/g, '.')}`;
    return 'Unknown';
}

function getBrowserContext() {
    const ua = navigator.userAgent;
    const browser = parseBrowser(ua);
    return {
        'browser.name': browser.name,
        'browser.version': browser.version,
        'os.name': parseOS(ua),
        'user_agent': ua,
        'screen.width': window.screen?.width,
        'screen.height': window.screen?.height,
        'language': navigator.language,
    };
}

// ─── Page Context ────────────────────────────────────────────────
function getPageContext() {
    return {
        'page.url': window.location.href,
        'page.path': window.location.pathname,
        'page.referrer': document.referrer || '',
        'page.title': document.title,
    };
}

// ─── User Context ────────────────────────────────────────────────
function getUserContext() {
    try {
        const state = store.getState();
        const userData = state?.userReducer?.userData;
        const sheet = state?.sheetReducer?.currentSelectedSheet;
        return {
            'user.email': userData?.email || '',
            'user.name': userData?.name || '',
            'sheet.id': sheet?.id || '',
            'sheet.name': sheet?.name || '',
        };
    } catch {
        return {};
    }
}

// ─── Trace Context ───────────────────────────────────────────────
function getTraceContext() {
    try {
        const span = trace.getSpan(context.active());
        if (!span) return {};
        const ctx = span.spanContext();
        return {
            'trace_id': ctx.traceId,
            'span_id': ctx.spanId,
        };
    } catch {
        return {};
    }
}

// ─── Session ID ──────────────────────────────────────────────────
let _sessionId = null;
function getSessionId() {
    if (!_sessionId) {
        _sessionId = `ses_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    }
    return _sessionId;
}

// ─── Stack Trace Capture ─────────────────────────────────────────
function captureStackTrace() {
    const err = new Error();
    const stack = err.stack || '';
    // Remove the first 3 lines (Error, captureStackTrace, and the calling log method)
    const lines = stack.split('\n');
    return lines.slice(3).join('\n');
}

function parseSourceLocation(stack) {
    // Extract first meaningful frame (skip internal/node_modules frames)
    const lines = (stack || '').split('\n');
    for (const line of lines) {
        const match = line.match(/at\s+(?:(.+?)\s+)?\(?(.+?):(\d+):(\d+)\)?/);
        if (match && !line.includes('node_modules') && !line.includes('browserLogger')) {
            return {
                'code.function': match[1] || 'anonymous',
                'code.filepath': match[2],
                'code.lineno': parseInt(match[3], 10),
                'code.colno': parseInt(match[4], 10),
            };
        }
    }
    return {};
}

// ─── Breadcrumbs ─────────────────────────────────────────────────
const MAX_BREADCRUMBS = 50;
const breadcrumbs = [];

function addBreadcrumb(crumb) {
    breadcrumbs.push({
        timestamp: new Date().toISOString(),
        ...crumb,
    });
    if (breadcrumbs.length > MAX_BREADCRUMBS) {
        breadcrumbs.shift();
    }
}

function getBreadcrumbsSnapshot() {
    return [...breadcrumbs];
}

// Auto-track route changes
function _trackNavigation() {
    const origPushState = history.pushState;
    const origReplaceState = history.replaceState;

    history.pushState = function (...args) {
        origPushState.apply(this, args);
        addBreadcrumb({ type: 'navigation', category: 'pushState', data: { to: args[2] } });
    };
    history.replaceState = function (...args) {
        origReplaceState.apply(this, args);
        addBreadcrumb({ type: 'navigation', category: 'replaceState', data: { to: args[2] } });
    };
    window.addEventListener('popstate', () => {
        addBreadcrumb({ type: 'navigation', category: 'popstate', data: { to: window.location.pathname } });
    });
}

// Auto-track clicks
function _trackClicks() {
    document.addEventListener('click', (e) => {
        const target = e.target;
        const selector = target.id ? `#${target.id}` : target.className ? `.${String(target.className).split(' ')[0]}` : target.tagName?.toLowerCase();
        const text = (target.textContent || '').slice(0, 50);
        addBreadcrumb({
            type: 'ui.click',
            category: 'ui',
            data: { selector, text, tag: target.tagName?.toLowerCase() },
        });
    }, { capture: true, passive: true });
}

// Auto-track fetch requests
function _trackFetch() {
    const origFetch = window.fetch;
    window.fetch = function (...args) {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
        const method = args[1]?.method || 'GET';

        // Don't track OTEL exports to avoid recursion
        if (url.includes('/otel/v1/')) {
            return origFetch.apply(this, args);
        }

        const startTime = Date.now();
        return origFetch.apply(this, args).then(
            (response) => {
                addBreadcrumb({
                    type: 'fetch',
                    category: 'http',
                    data: { url: url.slice(0, 200), method, status: response.status, duration_ms: Date.now() - startTime },
                });
                return response;
            },
            (error) => {
                addBreadcrumb({
                    type: 'fetch',
                    category: 'http',
                    data: { url: url.slice(0, 200), method, error: error.message, duration_ms: Date.now() - startTime },
                });
                throw error;
            }
        );
    };
}

// Initialize auto-tracking
_trackNavigation();
_trackClicks();
_trackFetch();

// ─── Severity Mapping ────────────────────────────────────────────
const SEVERITY_MAP = {
    DEBUG: SeverityNumber.DEBUG,
    INFO: SeverityNumber.INFO,
    WARN: SeverityNumber.WARN,
    ERROR: SeverityNumber.ERROR,
};

// ─── Core Log Function ──────────────────────────────────────────
function emitLog(level, message, extra = {}) {
    const attributes = {
        ...getBrowserContext(),
        ...getPageContext(),
        ...getUserContext(),
        ...getTraceContext(),
        'session.id': getSessionId(),
        ...extra,
    };

    // Capture source location for ALL log levels (file, line, function)
    const stackForLocation = attributes['error.stack'] || captureStackTrace();
    const sourceLocation = parseSourceLocation(stackForLocation);
    Object.assign(attributes, sourceLocation);

    // Include full stack trace for error/warn
    if (level === 'ERROR' || level === 'WARN') {
        attributes['code.stacktrace'] = attributes['error.stack'] || stackForLocation;
    }

    // Attach breadcrumbs for errors
    if (level === 'ERROR') {
        attributes['breadcrumbs'] = JSON.stringify(getBreadcrumbsSnapshot());
    }

    // Emit via OTEL logger
    logger.emit({
        severityNumber: SEVERITY_MAP[level] || SeverityNumber.INFO,
        severityText: level,
        body: message,
        attributes,
    });
}

// ─── Public API ──────────────────────────────────────────────────
export const browserLogger = {
    debug(message, extra = {}) {
        emitLog('DEBUG', message, extra);
    },

    info(message, extra = {}) {
        emitLog('INFO', message, extra);
    },

    warn(message, extra = {}) {
        emitLog('WARN', message, extra);
    },

    error(message, error, extra = {}) {
        const errorAttrs = { ...extra };
        if (error) {
            if (error instanceof Error) {
                errorAttrs['error.name'] = error.name;
                errorAttrs['error.message'] = error.message;
                errorAttrs['error.stack'] = error.stack;
            } else {
                errorAttrs['error.message'] = String(error);
            }
        }
        emitLog('ERROR', message, errorAttrs);
    },

    /** Add a custom breadcrumb */
    addBreadcrumb,
};

export default browserLogger;
